// Deep import to bypass bundler resolution issues in Docker/Linux
// Import from the local vendor folder created during the Docker build
import { Accelerometer } from "../vendor/motion-sensors.js";
import imageCompression from "browser-image-compression";

/**
 * Decrypts a string using a salt from environment variables.
 */
export const decrypt = (encoded) => {
  console.log("In decryption Method");
  const textToChars = (text) => text.split("").map((c) => c.charCodeAt(0));
  const applySaltToChar = (code) =>
    textToChars(process.env.REACT_APP_SALT).reduce((a, b) => a ^ b, code);
  return encoded
    .match(/.{1,2}/g)
    .map((hex) => parseInt(hex, 16))
    .map(applySaltToChar)
    .map((charCode) => String.fromCharCode(charCode))
    .join("");
};

/**
 * Calculates file size from a base64 data URL.
 */
export const getFileSize = (dataUrl) => {
  const base64Content = dataUrl.split(",")[1];
  const binary = Buffer.from(base64Content, "base64").toString("binary");
  const array = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    array[i] = binary.charCodeAt(i);
  }
  return new Blob([array]).size;
};

/**
 * Canvas drawing: Rounded Rectangle (Red)
 */
export const roundedRect = (ctx, x, y, width, height, radius) => {
  ctx.beginPath();
  ctx.lineWidth = 2;
  ctx.strokeStyle = "red";
  ctx.moveTo(x, y + radius);
  ctx.lineTo(x, y + height - radius);
  ctx.arcTo(x, y + height, x + radius, y + height, radius);
  ctx.lineTo(x + width - radius, y + height);
  ctx.arcTo(x + width, y + height, x + width, y + height - radius, radius);
  ctx.lineTo(x + width, y + radius);
  ctx.arcTo(x + width, y, x + width - radius, y, radius);
  ctx.lineTo(x + radius, y);
  ctx.arcTo(x, y, x, y + radius, radius);
  ctx.stroke();
};

/**
 * Canvas drawing: Text along an arc
 */
export const drawTextAlongArc = (context, str, centerX, centerY, radius, angle) => {
  context.save();
  context.font = "40px serif ";
  context.fillStyle = "white";
  context.translate(centerX, centerY);
  context.rotate((-1 * angle) / 2);
  context.rotate((-1 * (angle / str.length)) / 2);
  for (let n = 0; n < str.length; n++) {
    context.rotate(angle / str.length);
    context.save();
    context.translate(0, -1 * radius);
    context.fillText(str[n], 0, 0);
    context.restore();
  }
  context.restore();
};

/**
 * Canvas drawing: Inverse guidance lines (Yellow dashed)
 */
export const inverseGuidance = (ctx, width, height) => {
  ctx.beginPath();
  ctx.lineWidth = 5;
  ctx.strokeStyle = "yellow";
  ctx.setLineDash([10, 10]);
  ctx.moveTo(0, height / 4 + 60);
  ctx.quadraticCurveTo(width / 2, height / 4 - 50, width + 30, height / 4 + 70);
  ctx.moveTo(0, height / 2 + 70);
  ctx.quadraticCurveTo(width / 2, height / 2 - 50, width + 30, height / 2 + 70);
  ctx.stroke();
};

/**
 * Canvas drawing: Static guidance curves
 */
export const staticGuidance = (ctx, width, height) => {
  ctx.beginPath();
  ctx.lineWidth = 2;
  ctx.strokeStyle = "yellow";
  ctx.setLineDash([20, 10]);
  ctx.moveTo(0, height / 4 + 90);
  ctx.bezierCurveTo(20, 30, width, 30, width + 30, height / 4 + 90);
  ctx.stroke();
  ctx.moveTo(0, height / 1.5 + 100);
  ctx.bezierCurveTo(20, 150, width, 150, width + 30, height / 1.5 + 100);
  ctx.stroke();
};

/**
 * Converts dataURL to a File object.
 */
export const dataURLtoFile = (dataurl, filename) => {
  const arr = dataurl.split(",");
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
};

/**
 * Main motion detection logic using the Accelerometer polyfill.
 */
export const detectMotion = () => {
  let accelerometer = null;
  let prevX = null, prevY = null, prevZ = null;

  try {
    if (typeof Accelerometer === 'undefined') {
      console.warn("Accelerometer polyfill not available.");
      return;
    }
    
    accelerometer = new Accelerometer();
    accelerometer.start();
    
    accelerometer.onreading = () => {
      if (prevX !== null && (prevX !== accelerometer.x || prevY !== accelerometer.y || prevZ !== accelerometer.z)) {
        console.warn("Camera movement detected!");
      }
      prevX = accelerometer.x;
      prevY = accelerometer.y;
      prevZ = accelerometer.z;
    };

    accelerometer.onerror = (event) => {
      console.error("Sensor error:", event.error.name);
    };
  } catch (error) {
    console.error("Failed to initialize motion sensors:", error.message);
  }
};

/**
 * Compresses an image using browser-image-compression.
 */
export const compressImage = async (file, maxWidth) => {
  const options = { maxSizeMB: 4, maxWidthOrHeight: maxWidth, useWebWorker: false };
  try {
    const compressedImage = await imageCompression(file, options);
    const link = document.createElement("a");
    link.href = URL.createObjectURL(compressedImage);
    link.setAttribute("download", "compressed_image");
    link.click();
    return compressedImage;
  } catch (error) {
    console.error("Compression failed:", error);
  }
};

/**
 * Generates a timestamp string for file naming.
 */
export const getDateTime = (date) => {
  const pad = (num, size = 2) => String(num).padStart(size, "0");
  return `${pad(date.getDate())}${pad(date.getMonth() + 1)}${date.getFullYear()}${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}${pad(date.getMilliseconds(), 3)}_g`;
};

// Exporting remaining helper functions as requested
export const newRoundedRectWider = (ctx, width, height) => {
    ctx.beginPath();
    ctx.lineWidth = 5;
    ctx.strokeStyle = "red";
    ctx.moveTo(30, height / 4);
    ctx.arc(30, height / 4 - 30, 5, 0, 2 * Math.PI);
    ctx.stroke();
};

export const Capsule = (ctx, width) => {
    ctx.beginPath();
    ctx.setLineDash([0, 0]);
    let x = width / 2;
    ctx.moveTo(x - 25, 10);
    ctx.lineTo(x + 40, 10);
    ctx.quadraticCurveTo(x + 55, 20, x + 40, 30);
    ctx.lineTo(x - 25, 30);
    ctx.quadraticCurveTo(x - 40, 20, x - 25, 10);
    ctx.closePath();
    ctx.fillStyle = "#FF0000";
    ctx.strokeStyle = "#FF0000";
    ctx.fill();
    ctx.stroke();
};